import { CrashCheckpoints } from "./checkpoints.js";
import { NativeBridge } from "./native_bridge.js";
import type { CrashIdentityData } from "./types.js";

const STORAGE_KEY = 'bb_crash_identity_v1';

interface StoredIdentity
{
  user_id: string | null;
  device_id: string | null;
  analytics_id: string | null;
  consent: boolean;
}

export class CrashIdentity
{
  private static data: CrashIdentityData = { user_id: null, device_id: null, session_id: null, analytics_id: null };
  private static consent_allowed = false;

  public static init(): void
  {
    const stored = this.read();
    if ( !stored ) return;
    this.data.user_id = stored.user_id;
    this.data.device_id = stored.device_id;
    this.data.analytics_id = stored.analytics_id;
    this.consent_allowed = stored.consent;
    this.push();
  }

  public static setUser( user_id: string | number | null ): void
  {
    this.data.user_id = user_id == null ? null : String( user_id );
    this.push();
  }

  public static setDevice( device_id: string | null ): void
  {
    this.data.device_id = device_id;
    this.push();
  }

  public static setAnalyticsId( analytics_id: string | null ): void
  {
    this.data.analytics_id = analytics_id;
    this.push();
  }

  public static setSession( session_id: string | null ): void
  {
    this.data.session_id = session_id;
    CrashCheckpoints.setSessionId( session_id );
    this.push();
  }

  public static setConsentAllowed( allowed: boolean ): void
  {
    this.consent_allowed = allowed;
    this.push();
  }

  public static isConsentAllowed(): boolean
  {
    return this.consent_allowed;
  }

  public static get(): CrashIdentityData
  {
    return { ...this.data };
  }

  private static push(): void
  {
    NativeBridge.setIdentity( this.data, this.consent_allowed );
    try
    {
      const stored: StoredIdentity = { user_id: this.data.user_id, device_id: this.data.device_id, analytics_id: this.data.analytics_id, consent: this.consent_allowed };
      localStorage.setItem( STORAGE_KEY, JSON.stringify( stored ) );
    }
    catch {}
  }

  private static read(): StoredIdentity | null
  {
    try
    {
      const raw = localStorage.getItem( STORAGE_KEY );
      if ( !raw ) return null;
      const parsed = JSON.parse( raw ) as Partial<StoredIdentity>;
      return {
        user_id: typeof parsed.user_id === 'string' ? parsed.user_id : null,
        device_id: typeof parsed.device_id === 'string' ? parsed.device_id : null,
        analytics_id: typeof parsed.analytics_id === 'string' ? parsed.analytics_id : null,
        consent: parsed.consent === true,
      };
    }
    catch
    {
      return null;
    }
  }
}
